<template>
  <div v-if="consultationNode">
    
<FormKitLazyProvider config-file="true">
<WizardLayout
      :title="title"
      :lead="
        $texts(
          'wizard.consultationLead',
          'In wenigen Schritten können Sie direkt einen Beratungstermin vereinbaren.',
        )
      "
      :finish-title="
        $texts(
          'wizard_consultation_thankyou_title',
          'Vielen Dank für Ihre Anfrage!',
        )
      "
      :finish-lead="finishLead"
      :is-finished="isFinished"
      :node="consultationNode"
    >
      <FormKit
        id="consultation"
        type="form"
        name="consultation"
        :errors="errors"
        :actions="false"
        :input-errors="inputErrors"
        class=""
        use-local-storage
        @submit="createRequest"
      >
        <FormKit
          id="steps"
          v-slot="ctx"
          type="multi-step"
          name="steps"
          tab-style="tab"
          :allow-incomplete="false"
          wrapper-class="$remove:group"
          outer-class="$remove:group"
        >
          <WizardStepConsultationOptions :agencies="agencies" />
          <WizardStepPersonalDetails />
          <WizardStepContactData />
          <WizardStepConsultationReview
            :data="ctx?.value"
            :product="consultationNode.label"
            :agencies="agencies"
          />
        </FormKit>
      </FormKit>
    </WizardLayout>
</FormKitLazyProvider>

  </div>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { notNullish } from '@vueuse/core'
import type { FormKitNode } from '@formkit/core'
import type { FormKitOptionsItem } from '@formkit/inputs'
import type {
  ConsultationRequestInput,
  ConsultationWizardNodeFragment,
} from '#graphql-operations'

definePageMeta({
  name: 'wizard-consultation-form',
  path: '/beratungstermin/:id',
  layout: 'order',
})

const { dataLayerPush } = useAnalytics()
const { submit, isFinished, inputErrors, errors, entity } = useEntityMutation(
  'createConsultationRequest',
  {
    propertyPathMap: {
      post_code: 'steps.contact.postalCode',
      address: 'steps.contact.ddressLine1',
      city: 'steps.contact.locality',
      firstname: 'steps.personal.firstName',
      lastname: 'steps.personal.lastName',
      company: 'steps.personal.company',
      date_of_birth: 'steps.personal.birthdate',
      email: 'steps.contact.email',
      phonenumber: 'steps.contact.phone',
      message: 'steps.review.message',
      salutation: 'steps.personal.salutation',
      customer: 'steps.personal.isCustomer',
      availability: 'steps.contact.contact.customerAvailability',
    },
  },
)

useWizardState(isFinished)

const route = useRoute()

const id = computed(() => route.params.id as string)

const { data } = await useAsyncData(() =>
  useCachedGraphqlQuery('consultationWizardData', { id: id.value }).then(
    (v) => {
      if (v.data.agencies && v.data.consultationNode) {
        return v.data
      }
    },
  ),
)

const HIDDEN_AGENCIES = ['2', '4', '5', '6']

const agencies = computed<FormKitOptionsItem[]>(() =>
  (data.value?.agencies.items || [])
    .map((v) =>
      v &&
      'value' in v &&
      'label' in v &&
      v.value &&
      v.label &&
      !HIDDEN_AGENCIES.includes(v.value)
        ? {
            value: v.value,
            label: v.label,
          }
        : null,
    )
    .filter(notNullish),
)

const consultationNode = computed<ConsultationWizardNodeFragment | undefined>(
  () => {
    if (
      data.value?.consultationNode &&
      'label' in data.value.consultationNode
    ) {
      return data.value.consultationNode
    }
    return undefined
  },
)
const title = computed(
  () => 'Beratungstermin: ' + consultationNode.value?.label,
)

const { currentStepIndex } = useFormKitMultiStep([
  'appointment',
  'personal',
  'contact',
  'review',
])

await renderPageDependencies()

function mapOrderRequest(data: any): ConsultationRequestInput {
  const isCustomer = !!data.steps?.personal?.isCustomer
  const salutation = data.steps?.personal?.salutation
  const firstname = data.steps?.personal?.firstName
  const lastname = data.steps?.personal?.lastName
  const address = data.steps?.contact?.addressLine1
  const post_code = data.steps?.contact?.postalCode
  const city = data.steps?.contact?.locality
  const contact = data.steps?.contact?.contact?.meansOfContact
  const date = data.steps?.appointment?.date
  const time = data.steps?.appointment?.time
  const agency = data.steps?.appointment?.agency
  const date_of_birth = data.steps?.personal?.birthdate
  const email = data.steps?.contact?.email
  const phonenumber = data.steps?.contact?.phone
  const message = data.steps?.review?.message
  const page_title = consultationNode.value?.label
  const company = data.steps?.personal?.company
  const availability = data.steps?.contact?.contact?.customerAvailability
  return {
    customer: isCustomer ? 'existing' : 'new',
    salutation,
    firstname,
    lastname,
    address,
    post_code,
    city,
    contact,
    date,
    time,
    agency,
    date_of_birth,
    email,
    phonenumber,
    message,
    page_title,
    company,
    availability,
  }
}

const { $texts } = useEasyTexts()

const finishLead = computed(() => {
  return $texts(
    'wizard_consultation_thankyou_lead',
    'Wir werden Ihre Anfrage umgehend bearbeiten. Eine Bestätigung senden wir Ihnen in den nächsten Minuten an @email.',
  ).replace('@email', entity.value?.email || 'Ihre E-Mail Adresse')
})

async function createRequest(data: any, node: FormKitNode) {
  node.clearErrors()
  const done = await submit(mapOrderRequest(data))
  if (!done) {
    if ('restoreCache' in node) {
      // @ts-ignore
      node.restoreCache()
    }
  } else {
    dataLayerPush({
      event: 'bookAppointment',
      product: consultationNode.value?.label,
    })
  }
}

if (!consultationNode.value) {
  throw createError({
    statusCode: 404,
    fatal: true,
  })
}

onBeforeUnmount(() => {
  if (!isFinished.value) {
    dataLayerPush({
      event: 'abandonProcess',
      step: currentStepIndex.value,
    })
  }
})
</script>
